import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Copy,
  Send,
  Search,
  ShieldCheck,
  Check,
  X,
  Loader2,
  ChevronDown,
  Mail,
} from "lucide-react";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "") + "/api";

const SEGMENT_META = {
  hot_lead:      { label: "Hot Lead",      color: "text-amber-300 border-amber-500/40 bg-amber-500/[0.08]" },
  stalled_free:  { label: "Stalled Free",  color: "text-orange-300 border-orange-500/40 bg-orange-500/[0.08]" },
  signed_up_only:{ label: "Signed Up",     color: "text-zinc-400 border-zinc-700 bg-zinc-800/40" },
  cold_free:     { label: "Cold Free",     color: "text-sky-300 border-sky-500/40 bg-sky-500/[0.08]" },
  paid_active:   { label: "Paid · Active", color: "text-emerald-300 border-emerald-500/40 bg-emerald-500/[0.10]" },
  paid_stuck:    { label: "Paid · Stuck",  color: "text-yellow-300 border-yellow-500/40 bg-yellow-500/[0.08]" },
  paid_done:     { label: "Paid · Done",   color: "text-violet-300 border-violet-500/40 bg-violet-500/[0.10]" },
};

const TEMPLATES = [
  { id: "nudge_1_immediate", label: "Nudge 1 · Immediate" },
  { id: "nudge_2_price",     label: "Nudge 2 · Price math" },
  { id: "nudge_3_story",     label: "Nudge 3 · Why I wrote it" },
  { id: "nudge_4_last",      label: "Nudge 4 · Last touch" },
];

async function adminFetch(path, secret, opts = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      ...(opts.body ? { "Content-Type": "application/json" } : {}),
      "X-Admin-Secret": secret,
      ...(opts.headers || {}),
    },
  });
  const text = await res.text();
  let body = null;
  if (text) { try { body = JSON.parse(text); } catch { body = text; } }
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

function SegmentPill({ segment }) {
  const meta = SEGMENT_META[segment] || SEGMENT_META.signed_up_only;
  return (
    <span
      data-testid={`seg-pill-${segment}`}
      className={`inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider ${meta.color}`}
    >
      {meta.label}
    </span>
  );
}

function relativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffSec = (Date.now() - d.getTime()) / 1000;
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

// -----------------------------------------------------------------------------
// Password gate
// -----------------------------------------------------------------------------
function PasswordGate({ onAuth, initialError }) {
  const [pw, setPw] = useState("");
  const [error, setError] = useState(initialError || "");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await adminFetch("/admin/users", pw);
      onAuth(pw);
    } catch (err) {
      if (err.status === 403) setError("Invalid admin secret.");
      else setError(`Error: ${err?.body?.detail || err.message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main data-testid="admin-gate" className="min-h-screen flex items-center justify-center px-6">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl border border-amber-500/25 bg-zinc-950/70 backdrop-blur-md p-8 shadow-2xl"
      >
        <div className="flex items-center gap-2 text-amber-300 mb-6">
          <ShieldCheck className="h-4 w-4" />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em]">admin access</span>
        </div>
        <label className="block text-[11px] font-mono uppercase tracking-[0.18em] text-zinc-500 mb-2">
          Admin secret
        </label>
        <Input
          data-testid="admin-password-input"
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="••••••••"
          className="bg-zinc-900/60 border-amber-500/20 text-zinc-100 h-11"
          autoFocus
          disabled={busy}
        />
        {error && <p className="mt-3 text-xs text-red-400">{error}</p>}
        <Button
          data-testid="admin-password-submit"
          type="submit"
          disabled={busy || !pw}
          className="mt-5 w-full h-11 bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold gap-2"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          Unlock
        </Button>
      </form>
    </main>
  );
}

// -----------------------------------------------------------------------------
// Drawer — full user detail
// -----------------------------------------------------------------------------
function UserDrawer({ user, secret, onClose }) {
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user) return;
    let alive = true;
    (async () => {
      try {
        const d = await adminFetch(`/admin/user/${user.id}`, secret);
        if (alive) setDetail(d);
      } catch (e) {
        if (alive) setErr(e?.body?.detail || e.message);
      }
    })();
    return () => { alive = false; };
  }, [user, secret]);

  if (!user) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        aria-label="Close drawer"
        onClick={onClose}
        className="absolute inset-0 bg-black/70"
      />
      <aside
        data-testid="admin-user-drawer"
        className="relative w-full max-w-xl bg-zinc-950 border-l border-amber-500/20 overflow-y-auto p-8"
      >
        <button
          onClick={onClose}
          className="absolute top-5 right-5 text-zinc-500 hover:text-zinc-200"
          data-testid="admin-drawer-close"
        >
          <X className="h-5 w-5" />
        </button>
        <p className="font-mono text-[11px] uppercase tracking-widest text-amber-300">user detail</p>
        <h2 className="mt-2 text-2xl font-semibold text-zinc-50 break-all">{user.email}</h2>
        <p className="mt-1 text-sm text-zinc-400">{user.name} · <SegmentPill segment={user.segment} /></p>

        <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <dt className="text-zinc-500">Paid</dt>
          <dd className="text-zinc-200">{user.is_paid ? `Yes · ${user.payment_method || "?"}` : "No"}</dd>
          <dt className="text-zinc-500">Quests completed</dt>
          <dd className="text-zinc-200">{user.quests_completed} / 13</dd>
          <dt className="text-zinc-500">Total XP</dt>
          <dd className="text-zinc-200">{user.total_xp}</dd>
          <dt className="text-zinc-500">Avg score</dt>
          <dd className="text-zinc-200">{user.avg_score ?? "—"}</dd>
          <dt className="text-zinc-500">Q1 score</dt>
          <dd className="text-zinc-200">{user.q1_score ?? "—"}</dd>
          <dt className="text-zinc-500">Persona</dt>
          <dd className="text-zinc-200">{user.founder_persona || "—"}</dd>
          <dt className="text-zinc-500">Stage</dt>
          <dd className="text-zinc-200">{user.startup_stage || "—"}</dd>
          <dt className="text-zinc-500">Last activity</dt>
          <dd className="text-zinc-200">{relativeTime(user.last_activity_at)}</dd>
          <dt className="text-zinc-500">Signed up</dt>
          <dd className="text-zinc-200">{relativeTime(user.created_at)}</dd>
          <dt className="text-zinc-500">Email opt-out</dt>
          <dd className="text-zinc-200">{user.email_opt_out ? "Yes" : "No"}</dd>
        </dl>

        {user.startup_description && (
          <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
            <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">description</p>
            <p className="text-sm text-zinc-200 whitespace-pre-wrap break-words">{user.startup_description}</p>
          </div>
        )}

        <h3 className="mt-8 font-mono text-[11px] uppercase tracking-widest text-amber-300">
          quest progress
        </h3>
        {err && <p className="mt-2 text-xs text-red-400">{err}</p>}
        <div className="mt-3 space-y-1.5">
          {(detail?.progress || []).map((p) => (
            <div
              key={p.quest_id}
              className="flex items-center justify-between text-sm text-zinc-300 border border-zinc-800 rounded px-3 py-2"
            >
              <span>
                <span className="font-mono text-zinc-500 text-xs mr-2">Q{String(p.quest_id).padStart(2, "0")}</span>
                {p.status}
              </span>
              <span className="text-xs text-zinc-500">
                {p.score != null ? `${Math.round(p.score)} · ` : ""}
                {(p.messages || []).length} msgs
              </span>
            </div>
          ))}
        </div>

        <h3 className="mt-8 font-mono text-[11px] uppercase tracking-widest text-amber-300">
          emails sent
        </h3>
        <div className="mt-3 space-y-1.5">
          {(detail?.emails_sent || []).length === 0 && (
            <p className="text-xs text-zinc-500">No emails sent yet.</p>
          )}
          {(detail?.emails_sent || []).map((s) => (
            <div
              key={s.id || s._id}
              className="flex items-center justify-between text-xs border border-zinc-800 rounded px-3 py-2"
            >
              <span className="text-zinc-300">{s.template_id}</span>
              <span className={`font-mono ${s.status === "sent" ? "text-emerald-400" : "text-red-400"}`}>
                {s.status} · {relativeTime(s.sent_at)}
              </span>
            </div>
          ))}
        </div>
      </aside>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main admin table
// -----------------------------------------------------------------------------
function AdminTable({ secret, onKick }) {
  const [users, setUsers] = useState([]);
  const [segCounts, setSegCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [segFilter, setSegFilter] = useState("all");
  const [openUser, setOpenUser] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [automation, setAutomation] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await adminFetch("/admin/automation-status", secret);
        if (alive) setAutomation(s);
      } catch { /* non-blocking */ }
    })();
    return () => { alive = false; };
  }, [secret, refreshKey]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const data = await adminFetch("/admin/users", secret);
        if (!alive) return;
        setUsers(data.users || []);
        setSegCounts(data.segments || {});
      } catch (e) {
        if (e.status === 403) onKick("Session expired — re-enter secret.");
        else toast.error(`Load failed: ${e?.body?.detail || e.message}`);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [secret, refreshKey, onKick]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return users.filter((u) => {
      if (segFilter !== "all" && u.segment !== segFilter) return false;
      if (s) {
        const hit =
          (u.email || "").toLowerCase().includes(s) ||
          (u.name || "").toLowerCase().includes(s);
        if (!hit) return false;
      }
      return true;
    });
  }, [users, search, segFilter]);

  async function sendNudge(user, templateId) {
    setSendingId(`${user.id}:${templateId}`);
    try {
      const resp = await adminFetch("/admin/send-email", secret, {
        method: "POST",
        body: JSON.stringify({ user_id: user.id, template_id: templateId }),
      });
      if (resp.skipped === "already_sent") {
        toast.info(`Skipped: ${user.email} already got ${templateId}`);
      } else {
        toast.success(`Sent ${templateId} → ${user.email}`);
      }
      setRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error(`Send failed: ${e?.body?.detail || e.message}`);
    } finally {
      setSendingId(null);
    }
  }

  async function bulkNudge() {
    if (segFilter !== "hot_lead") return;
    if (!window.confirm(`Send Nudge 1 to ${filtered.length} hot leads?`)) return;
    setBulkBusy(true);
    let sent = 0;
    let skipped = 0;
    let failed = 0;
    for (const u of filtered) {
      try {
        const resp = await adminFetch("/admin/send-email", secret, {
          method: "POST",
          body: JSON.stringify({ user_id: u.id, template_id: "nudge_1_immediate" }),
        });
        if (resp.skipped === "already_sent") skipped += 1;
        else sent += 1;
      } catch {
        failed += 1;
      }
    }
    toast.success(`Bulk done · sent ${sent} · skipped ${skipped}${failed ? ` · failed ${failed}` : ""}`);
    setBulkBusy(false);
    setRefreshKey((k) => k + 1);
  }

  function copyEmails() {
    const csv = filtered.map((u) => u.email).filter(Boolean).join(", ");
    navigator.clipboard?.writeText(csv);
    toast.success(`Copied ${filtered.length} emails.`);
  }

  return (
    <main data-testid="admin-page" className="min-h-screen px-6 sm:px-10 py-10">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-wrap items-baseline justify-between gap-4 mb-6">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-amber-300">
              founder mode · admin
            </p>
            <div className="mt-1 flex flex-wrap items-baseline gap-3">
              <h1 className="text-3xl sm:text-4xl font-semibold text-zinc-50">
                {users.length} founders
              </h1>
              {automation && (
                <span
                  data-testid="admin-automation-pill"
                  title={
                    automation.enabled
                      ? `Nudge cron every ${automation.interval_minutes}min · cap ${automation.rate_cap_per_pass}/pass`
                      : "Nudge cron is paused via EMAIL_AUTOMATION_ENABLED"
                  }
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider ${
                    automation.enabled && automation.scheduler_running
                      ? "border-emerald-500/40 bg-emerald-500/[0.10] text-emerald-300"
                      : "border-zinc-700 bg-zinc-900/60 text-zinc-500"
                  }`}
                >
                  <span
                    className={`inline-block h-1.5 w-1.5 rounded-full ${
                      automation.enabled && automation.scheduler_running
                        ? "bg-emerald-400 animate-pulse"
                        : "bg-zinc-600"
                    }`}
                  />
                  automation: {automation.enabled && automation.scheduler_running ? "on" : "off"}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setRefreshKey((k) => k + 1)}
              variant="outline"
              size="sm"
              className="border-zinc-700 text-zinc-200 hover:bg-zinc-900"
              data-testid="admin-refresh"
            >
              Refresh
            </Button>
            <Button
              onClick={() => onKick("Signed out.")}
              variant="outline"
              size="sm"
              className="border-zinc-700 text-zinc-200 hover:bg-zinc-900"
              data-testid="admin-signout"
            >
              Sign out
            </Button>
          </div>
        </div>

        {/* Segment cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          {Object.keys(SEGMENT_META).map((seg) => {
            const active = segFilter === seg;
            const count = segCounts[seg] || 0;
            const meta = SEGMENT_META[seg];
            return (
              <button
                key={seg}
                onClick={() => setSegFilter(active ? "all" : seg)}
                data-testid={`admin-seg-card-${seg}`}
                className={`rounded-xl border p-3 text-left transition ${
                  active
                    ? "border-amber-400 bg-amber-500/[0.10] ring-1 ring-amber-400/40"
                    : "border-zinc-800 bg-zinc-950/40 hover:border-zinc-700"
                }`}
              >
                <div className="font-mono text-2xl text-zinc-50">{count}</div>
                <div className={`mt-1 inline-flex rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider ${meta.color}`}>
                  {meta.label}
                </div>
              </button>
            );
          })}
        </div>

        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              data-testid="admin-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or email…"
              className="pl-9 bg-zinc-900/60 border-zinc-800 text-zinc-100 h-10"
            />
          </div>
          <Select value={segFilter} onValueChange={setSegFilter}>
            <SelectTrigger data-testid="admin-segment-filter" className="w-48 bg-zinc-900/60 border-zinc-800 text-zinc-100 h-10">
              <SelectValue placeholder="All segments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All segments</SelectItem>
              {Object.keys(SEGMENT_META).map((seg) => (
                <SelectItem key={seg} value={seg}>
                  {SEGMENT_META[seg].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={copyEmails}
            variant="outline"
            className="border-zinc-700 text-zinc-200 hover:bg-zinc-900 gap-2"
            data-testid="admin-copy-emails"
          >
            <Copy className="h-4 w-4" /> Copy emails ({filtered.length})
          </Button>
          {segFilter === "hot_lead" && filtered.length > 0 && (
            <Button
              onClick={bulkNudge}
              disabled={bulkBusy}
              className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold gap-2"
              data-testid="admin-bulk-nudge"
            >
              {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send Nudge 1 to all hot leads
            </Button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-zinc-800">
          <table data-testid="admin-users-table" className="min-w-full text-sm">
            <thead className="bg-zinc-900/60 text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Email</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Segment</th>
                <th className="text-right px-4 py-3">Q / 13</th>
                <th className="text-right px-4 py-3">XP</th>
                <th className="text-right px-4 py-3">Score</th>
                <th className="text-left px-4 py-3">Paid</th>
                <th className="text-left px-4 py-3">Last Active</th>
                <th className="text-left px-4 py-3">Sent</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900">
              {loading && (
                <tr>
                  <td colSpan={10} className="text-center py-16 text-zinc-500">
                    <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center py-16 text-zinc-500">
                    No founders matched.
                  </td>
                </tr>
              )}
              {!loading && filtered.map((u) => {
                const sentTemplateIds = new Set((u.emails_sent || []).filter((s) => s.status === "sent").map((s) => s.template_id));
                const receiptSent = sentTemplateIds.has("purchase_confirmation");
                return (
                  <tr
                    key={u.id}
                    data-testid={`admin-user-row-${u.id}`}
                    className="hover:bg-zinc-900/40 transition-colors cursor-pointer"
                    onClick={() => setOpenUser(u)}
                  >
                    <td className="px-4 py-3 text-zinc-100 break-all">{u.email}</td>
                    <td className="px-4 py-3 text-zinc-300">{u.name}</td>
                    <td className="px-4 py-3"><SegmentPill segment={u.segment} /></td>
                    <td className="px-4 py-3 text-right font-mono text-zinc-300">{u.quests_completed} / 13</td>
                    <td className="px-4 py-3 text-right font-mono text-zinc-300">{u.total_xp}</td>
                    <td className="px-4 py-3 text-right font-mono text-zinc-300">
                      {u.avg_score != null ? u.avg_score : "—"}
                    </td>
                    <td className="px-4 py-3">
                      {u.is_paid
                        ? <span className="text-emerald-400 font-mono text-xs inline-flex items-center gap-1">
                            ✓ {u.payment_method}
                            {receiptSent && (
                              <span
                                title="Purchase confirmation email delivered"
                                data-testid={`admin-receipt-sent-${u.id}`}
                                className="inline-flex items-center gap-0.5 rounded border border-emerald-500/40 bg-emerald-500/[0.10] px-1 py-0.5 text-[9px] uppercase tracking-wider text-emerald-300"
                              >
                                <Mail className="h-2.5 w-2.5" /> receipt
                              </span>
                            )}
                          </span>
                        : <span className="text-zinc-600 font-mono text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">{relativeTime(u.last_activity_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {TEMPLATES.map((t, i) => (
                          <span
                            key={t.id}
                            title={sentTemplateIds.has(t.id) ? `Sent: ${t.label}` : `Not sent: ${t.label}`}
                            className={`inline-flex h-5 w-5 items-center justify-center rounded font-mono text-[10px] ${
                              sentTemplateIds.has(t.id)
                                ? "bg-emerald-500/[0.15] text-emerald-300 border border-emerald-500/40"
                                : "bg-zinc-900 text-zinc-600 border border-zinc-800"
                            }`}
                          >
                            {sentTemplateIds.has(t.id) ? <Check className="h-3 w-3" /> : i + 1}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-500/40 bg-amber-500/[0.05] text-amber-300 hover:bg-amber-500/[0.15] gap-1 h-8"
                            data-testid={`admin-send-nudge-${u.id}`}
                            disabled={sendingId?.startsWith(u.id)}
                          >
                            {sendingId?.startsWith(u.id) ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                            Nudge <ChevronDown className="h-3 w-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-zinc-950 border-amber-500/30">
                          {TEMPLATES.map((t) => (
                            <DropdownMenuItem
                              key={t.id}
                              data-testid={`admin-send-${u.id}-${t.id}`}
                              onClick={() => sendNudge(u, t.id)}
                              className="text-zinc-200 focus:bg-amber-500/[0.10] focus:text-amber-200"
                            >
                              {sentTemplateIds.has(t.id) && <Check className="h-3 w-3 mr-2 text-emerald-400" />}
                              {t.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {openUser && <UserDrawer user={openUser} secret={secret} onClose={() => setOpenUser(null)} />}
    </main>
  );
}

// -----------------------------------------------------------------------------
// Top-level
// -----------------------------------------------------------------------------
export default function AdminPage() {
  const [secret, setSecret] = useState(null);
  const [kickError, setKickError] = useState("");

  if (!secret) {
    return (
      <PasswordGate
        initialError={kickError}
        onAuth={(pw) => { setSecret(pw); setKickError(""); }}
      />
    );
  }
  return (
    <AdminTable
      secret={secret}
      onKick={(msg) => { setSecret(null); setKickError(msg); }}
    />
  );
}
